/* ---------------------------------------------------------------------------
   ACC UGC Videos
   Hardcoded winner of A/B test ACC_ThrivinEU_Product_25 (Variation 2).

   One video plays at a time and advances on a timer. Rotation only runs while
   the row is on screen, the tab is focused and the preview is closed. Tapping
   a circle opens that video fullscreen.

   Markup comes from snippets/acc-ugc-videos.liquid, so this file only drives
   behaviour. Swiper is the copy already bundled in third-parties.js.
--------------------------------------------------------------------------- */

{
  const SELECTOR = '.acc-ugc';
  const DESKTOP = matchMedia('(min-width: 768px)');
  const CALM = matchMedia('(prefers-reduced-motion: reduce)');
  const ACTIVE = 'acc-ugc-modal__video--active';
  const mounted = new WeakMap();

  // Resolves once the player has a frame to show, so the cross-fade never
  // reveals a black box. Capped, so a stalled network cannot wedge the modal.
  const firstFrame = (video) => new Promise((resolve) => {
    if (video.readyState >= 2) {
      resolve();
      return;
    }
    const done = () => {
      clearTimeout(timer);
      video.removeEventListener('loadeddata', done);
      resolve();
    };
    const timer = setTimeout(done, 2000);
    video.addEventListener('loadeddata', done, { once: true });
  });

  // Autoplay needs muted set as a property, not just an attribute, in Safari.
  const play = (video) => {
    if (!video) return;
    video.muted = true;
    video.playsInline = true;
    video.play()?.catch(() => {}); // Refused. The poster stays, which is fine.
  };

  // third-parties.js is deferred and earlier in the document, so Swiper is
  // normally already here. The wait is a safety net, not the usual path.
  const swiperReady = async () => {
    for (let i = 0; i < 100 && !window.Swiper; i += 1) {
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
    return Boolean(window.Swiper);
  };

  class UgcVideos {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('.acc-ugc__swiper');
      this.pagination = root.querySelector('.acc-ugc__pagination');
      this.modal = root.querySelector('.acc-ugc-modal');
      this.modalBuffers = [...(this.modal?.querySelectorAll('.acc-ugc-modal__video') ?? [])];
      this.slides = [...root.querySelectorAll('.acc-ugc__slide')];
      this.videos = this.slides.map((slide) => slide.querySelector('.acc-ugc__video'));

      this.every = Number(root.dataset.rotateInterval) || 5000;
      this.index = 0;
      this.modalIndex = 0;
      this.modalToken = 0;
      this.timer = null;
      this.onScreen = false;
      this.aborter = new AbortController();

      this.listen(root.querySelector('.acc-ugc__section'));
      this.mountSwiper();
    }

    get rotatable() {
      return this.onScreen && !document.hidden && !CALM.matches && !this.modal?.open;
    }

    // Videos are preload="none", so pointing src at the right rendition costs
    // nothing until something actually plays.
    applySource(video) {
      const next = DESKTOP.matches ? video.dataset.srcDesktop : video.dataset.srcMobile;
      if (!next || video.getAttribute('src') === next) return;
      const resume = !video.paused;
      video.src = next;
      if (resume) play(video);
    }

    show(index) {
      const total = this.videos.length;
      if (!total) return;

      this.index = (index + total) % total;

      this.slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.classList.toggle('acc-ugc__slide--active', active);
        if (active) return;
        this.videos[i].pause();
        this.videos[i].currentTime = 0;
      });

      play(this.videos[this.index]);
      this.track?.swiper?.slideTo(this.index, 350);
    }

    start() {
      if (!this.rotatable) return;
      this.videos.forEach((video) => this.applySource(video));
      clearInterval(this.timer);
      this.show(this.index);
      this.timer = setInterval(() => {
        if (this.rotatable) this.show(this.index + 1);
      }, this.every);
    }

    stop() {
      clearInterval(this.timer);
      this.timer = null;
      this.videos.forEach((video) => {
        video.pause();
        video.currentTime = 0;
      });
      this.slides.forEach((slide) => slide.classList.remove('acc-ugc__slide--active'));
    }

    openModal(index) {
      if (!this.modal?.showModal) return;
      this.stop();
      this.modal.showModal();
      document.documentElement.classList.add('acc-ugc-modal-open');
      this.showInModal(index);
    }

    // Loads the clip into whichever player is hidden, waits for its first frame,
    // then cross-fades. The token drops responses the viewer has already clicked
    // past, so holding down next cannot land an out-of-order video.
    async showInModal(index) {
      const total = this.videos.length;
      if (!this.modalBuffers.length || !total) return;

      this.modalIndex = (index + total) % total;
      this.modalToken += 1;
      const token = this.modalToken;

      const current = this.modalBuffers.find((video) => video.classList.contains(ACTIVE));
      const next = this.modalBuffers.find((video) => video !== current) ?? this.modalBuffers[0];
      const { srcDesktop, srcMobile } = this.videos[this.modalIndex].dataset;

      // preload="none" means nothing is fetched until we ask, so start the load
      // AND playback first. Waiting on loadeddata beforehand would just stall
      // until the timeout, because no load would ever have been kicked off.
      next.preload = 'auto';
      next.src = srcDesktop || srcMobile;
      play(next);

      await firstFrame(next);
      if (token !== this.modalToken || !this.modal.open) return;

      next.classList.add(ACTIVE);
      current?.classList.remove(ACTIVE);
      current?.pause();
    }

    listen(section) {
      const { signal } = this.aborter;

      this.root.addEventListener('click', ({ target }) => {
        if (target.closest('.acc-ugc-modal')) return;
        const media = target.closest('.acc-ugc__media');
        if (media) this.openModal(Number(media.dataset.index));
      }, { signal });

      this.modal?.addEventListener('click', ({ target }) => {
        // A click landing on the dialog itself came from the backdrop.
        if (target === this.modal || target.closest('.acc-ugc-modal__close')) this.modal.close();
        else if (target.closest('.acc-ugc-modal__prev')) this.showInModal(this.modalIndex - 1);
        else if (target.closest('.acc-ugc-modal__next')) this.showInModal(this.modalIndex + 1);
      }, { signal });

      this.modal?.addEventListener('keydown', (event) => {
        const step = { ArrowLeft: -1, ArrowRight: 1 }[event.key]; // Escape closes natively.
        if (!step) return;
        event.preventDefault();
        this.showInModal(this.modalIndex + step);
      }, { signal });

      this.modal?.addEventListener('close', () => {
        this.modalToken += 1; // Cancel any load still in flight.
        this.resetModal();
        document.documentElement.classList.remove('acc-ugc-modal-open');
        // Pick the rotation back up on the clip they were just watching. Without
        // this it resumes wherever it had got to before the preview opened, and
        // the carousel visibly scrolls backwards.
        this.index = this.modalIndex;
        this.start();
      }, { signal });

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.stop();
        else this.start();
      }, { signal });

      // Only crossing the breakpoint matters, so this replaces a resize handler.
      // Swiper re-measures itself on resize already.
      DESKTOP.addEventListener('change', () => {
        this.videos.forEach((video) => this.applySource(video));
      }, { signal });

      if (!section) return;
      this.watcher = new IntersectionObserver(([entry]) => {
        this.onScreen = entry.isIntersecting;
        if (this.onScreen) this.start();
        else this.stop();
      }, { threshold: 0.1 });
      this.watcher.observe(section);
    }

    async mountSwiper() {
      if (!this.track || !this.pagination) return;
      if (!(await swiperReady())) return; // Row still scrolls natively.
      if (!this.track.isConnected || this.track.swiper) return;

      new window.Swiper(this.track, {
        slidesPerView: 'auto',
        spaceBetween: 8,
        grabCursor: true,
        watchOverflow: true,
        resistanceRatio: 0.85,
        preventClicks: false,
        preventClicksPropagation: false,
        noSwipingSelector: '.swiper-no-swiping',
        pagination: {
          el: this.pagination,
          clickable: true,
          bulletClass: 'acc-ugc__dot',
          bulletActiveClass: 'acc-ugc__dot--active',
        },
      });

      this.track.swiper?.slideTo(this.index, 0);
    }

    resetModal() {
      this.modalBuffers.forEach((video) => {
        video.pause();
        video.classList.remove(ACTIVE);
        video.removeAttribute('src');
      });
    }

    destroy() {
      this.stop();
      this.resetModal();
      this.aborter.abort();
      this.watcher?.disconnect();
      this.track?.swiper?.destroy(true, true);
      document.documentElement.classList.remove('acc-ugc-modal-open');
      mounted.delete(this.root);
    }
  }

  const mount = (scope = document) => {
    for (const root of scope.querySelectorAll?.(SELECTOR) ?? []) {
      if (!mounted.has(root)) mounted.set(root, new UgcVideos(root));
    }
  };

  mount();

  // The theme editor re-renders the whole section around the block.
  document.addEventListener('shopify:section:load', ({ target }) => mount(target));
  document.addEventListener('shopify:section:unload', ({ target }) => {
    for (const root of target.querySelectorAll?.(SELECTOR) ?? []) mounted.get(root)?.destroy();
  });
}
